import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Panel, SectionTitle } from "@/components/sov";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, KeyRound, Smartphone, AlertTriangle, Check } from "lucide-react";

const Mfa = () => {
  const { user, refreshMe } = useAuth();
  const [stage, setStage] = useState(user?.mfa_enabled ? "enrolled" : "intro");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [devices, setDevices] = useState([]);

  useEffect(() => {
    api.get("/auth/devices").then((r) => setDevices(r.data.devices || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.mfa_enabled) setStage("enrolled");
  }, [user?.mfa_enabled]);

  const beginSetup = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetup(data);
      setStage("setup");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/mfa/confirm", { code });
      setCode("");
      setSetup(null);
      await refreshMe();
      setStage("enrolled");
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const disableMfa = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/mfa/disable", { password: pwd });
      setPwd("");
      await refreshMe();
      setStage("intro");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not disable MFA");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="mfa-page">
      <SectionTitle sub="// SECOND-FACTOR ENROLMENT & DEVICE ROSTER">
        MFA · Sovereign Identity
      </SectionTitle>

      <Panel
        testid="panel-mfa"
        title="Multi-Factor Authentication"
        code="§ MFA"
        action={
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3 flex items-center gap-2">
            <ShieldCheck size={11} className="text-gold-300" />
            {user?.mfa_enabled ? "ENROLLED" : "NOT ENROLLED"}
          </span>
        }
      >
        <div className="p-6">
          {stage === "intro" && (
            <div className="space-y-5" data-testid="mfa-intro">
              <p className="font-serif italic text-[14px] text-ivory-2 leading-relaxed">
                "A single factor is a single key. A second factor is a sovereign vow.
                Sentinel will demand it whenever risk crosses the threshold."
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { n: 1, t: "Open Authenticator", d: "Google Authenticator, 1Password, Authy, or any TOTP app" },
                  { n: 2, t: "Scan the Sigil", d: "Add a new account by scanning the rotating QR" },
                  { n: 3, t: "Confirm Code", d: "Enter the 6-digit code to seal the enrolment" },
                ].map((s) => (
                  <div key={s.n} className="border border-[color:var(--gold-line)] p-4">
                    <div className="font-display text-[26px] text-gold-300">{s.n}</div>
                    <div className="font-mono text-[11px] text-gold-100 tracking-[0.14em] uppercase mt-1">{s.t}</div>
                    <div className="font-serif italic text-[12px] text-ivory-2 mt-2">{s.d}</div>
                  </div>
                ))}
              </div>
              {error && (
                <div className="border border-[rgba(255,122,110,0.4)] bg-[rgba(200,68,59,0.08)] px-3 py-2 flex items-start gap-2">
                  <AlertTriangle size={12} className="mt-1" style={{ color: "#ff7a6e" }} />
                  <span className="font-mono text-[11px]" style={{ color: "#ffaba2" }}>{error}</span>
                </div>
              )}
              <button onClick={beginSetup} disabled={busy} className="btn-gold" data-testid="mfa-begin">
                <KeyRound size={11} />
                {busy ? "Forging Sigil…" : "Begin Enrolment"}
              </button>
            </div>
          )}

          {stage === "setup" && setup && (
            <form onSubmit={confirmSetup} className="space-y-5" data-testid="mfa-setup">
              <div className="flex items-start gap-6">
                <div className="border border-[color:var(--gold-line)] p-3 bg-white/95 shrink-0">
                  <img src={setup.qr_data_url} alt="MFA QR" className="w-[180px] h-[180px]" data-testid="mfa-qr" />
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Issuer</div>
                    <div className="font-display text-[15px] text-gold-100">{setup.issuer}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Manual Secret</div>
                    <code className="font-mono text-[12px] text-gold-100 break-all" data-testid="mfa-secret">
                      {setup.secret}
                    </code>
                  </div>
                  <div className="font-serif italic text-[12px] text-ivory-2 leading-snug">
                    Scan the sigil with your authenticator app. Once it issues a 6-digit code, enter it below to seal enrolment.
                  </div>
                </div>
              </div>
              <div>
                <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Confirmation Code</label>
                <input
                  data-testid="mfa-confirm-code"
                  type="text"
                  pattern="[0-9]{6}"
                  inputMode="numeric"
                  required
                  className="sov-input mt-1 font-mono text-center tracking-[0.5em] text-[20px] max-w-[260px]"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  autoFocus
                />
              </div>
              {error && (
                <div className="border border-[rgba(255,122,110,0.4)] bg-[rgba(200,68,59,0.08)] px-3 py-2 flex items-start gap-2">
                  <AlertTriangle size={12} className="mt-1" style={{ color: "#ff7a6e" }} />
                  <span className="font-mono text-[11px]" style={{ color: "#ffaba2" }}>{error}</span>
                </div>
              )}
              <div className="flex gap-3">
                <button type="submit" disabled={busy || code.length !== 6} className="btn-gold" data-testid="mfa-confirm-btn">
                  <Check size={11} />
                  {busy ? "Sealing…" : "Seal Enrolment"}
                </button>
                <button type="button" onClick={() => setStage("intro")} className="btn-ghost">Cancel</button>
              </div>
            </form>
          )}

          {stage === "enrolled" && (
            <div className="space-y-5" data-testid="mfa-enrolled">
              <div className="flex items-center gap-3 px-4 py-3 border border-[rgba(108,200,143,0.4)] bg-[rgba(108,200,143,0.06)]">
                <ShieldCheck size={16} style={{ color: "#6cc88f" }} />
                <div>
                  <div className="font-display text-[14px] text-gold-100 tracking-[0.1em]">MFA ACTIVE</div>
                  <div className="font-serif italic text-[12px] text-ivory-2">
                    Sentinel will demand a second factor on every elevated-risk login.
                  </div>
                </div>
              </div>
              <form onSubmit={disableMfa} className="space-y-3">
                <div>
                  <label className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Confirm Password to Disable MFA</label>
                  <input
                    data-testid="mfa-disable-pwd"
                    type="password"
                    required
                    className="sov-input mt-1 max-w-[400px]"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                  />
                </div>
                {error && (
                  <div className="border border-[rgba(255,122,110,0.4)] bg-[rgba(200,68,59,0.08)] px-3 py-2 flex items-start gap-2">
                    <AlertTriangle size={12} className="mt-1" style={{ color: "#ff7a6e" }} />
                    <span className="font-mono text-[11px]" style={{ color: "#ffaba2" }}>{error}</span>
                  </div>
                )}
                <button type="submit" disabled={busy || !pwd} className="btn-ghost" data-testid="mfa-disable-btn">
                  Disable MFA
                </button>
              </form>
            </div>
          )}
        </div>
      </Panel>

      <Panel
        testid="panel-devices"
        title="Recognised Devices"
        code="§ MFA.B"
        action={
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3 flex items-center gap-2">
            <Smartphone size={11} className="text-gold-300" />
            {devices.length} fingerprint{devices.length === 1 ? "" : "s"}
          </span>
        }
      >
        <table className="sov-table">
          <thead>
            <tr><th>Device FP</th><th>UA</th><th>Screen</th><th>Sightings</th><th>First Seen</th><th>Last Seen</th></tr>
          </thead>
          <tbody>
            {devices.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-ivory-3 italic">No baseline yet — first login will register this device.</td></tr>
            )}
            {devices.map((d) => (
              <tr key={d.device_fp} data-testid={`device-${d.device_fp.slice(0,8)}`}>
                <td className="text-gold-100">{d.device_fp.slice(0, 16)}…</td>
                <td className="text-ivory-2 truncate max-w-[280px]">{(d.ua || "").slice(0, 60)}</td>
                <td className="text-ivory-2">{d.screen}</td>
                <td className="text-gold-200">{d.sightings}</td>
                <td className="text-ivory-3">{d.first_seen?.slice(0, 16)}</td>
                <td className="text-ivory-3">{d.last_seen?.slice(0, 16)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
};

export default Mfa;
